import React, { useState, useEffect } from 'react';
import { FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa';
import Navbar from '../../components/layout/Navbar';
import PageContainer from '../../components/layout/PageContainer';
import { aboutApi, TeamMember } from '../../lib/aboutApi';
import Spinner from '../../components/common/Spinner';


const AboutPage: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTeamMembers = async () => {
      try {
        const members = await aboutApi.getTeamMembers();
        const sorted = members.sort((a, b) => {
          if (a.name === 'Justin Burrell') return -1;
          if (b.name === 'Justin Burrell') return 1;
          if (a.name === 'David Okeh') return -1;
          if (b.name === 'David Okeh') return 1;
          return 0;
        });
        setTeamMembers(sorted);
      } catch (error) {
        console.error('Failed to load team members:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTeamMembers();
  }, []);
  return (
    <div className="min-h-screen">
      <Navbar />

      <PageContainer>
        {/* Main heading */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#4b2f23] mb-4">
            The MVP Production Process 
          </h1>
        </div>

        {/* Hackathon section */}
        <div className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1 flex justify-center">
            <img
              src="https://smgstyptklhcmtzfwjxn.supabase.co/storage/v1/object/public/about-profile/MountainMerge%20Team%20Photo.jpeg"
              alt="MountainMerge Team Photo"
              className="w-64 h-auto rounded-2xl shadow-lg"
            />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This was a passion project created by two engineering students during the Fall 2025 Lehigh AI Club Hackathon with Cisco and Redbull. 
              We set out to build a collaborative, AI-powered study platform that helps students capture lecture content, transform it into clean 
              structured notes, automatically generate flashcards, and collaborate inside shared class workspaces.
            </p>
            <div className="flex justify-center">
              <a
                href="https://github.com/JustinBurrell/mountain-merge-mvp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                {/* @ts-ignore - react-icons has known TypeScript strict mode issue in Vercel builds */}
                <FaGithub size={18} />
                <span>Source Code</span>
              </a>
            </div>
          </div>
        </div>

        {/* Features section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-[#4b2f23] mb-8 text-center">Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-card p-6 border border-white/60">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Classes</h3>
              <p className="text-gray-600">Join and organize your courses in dedicated class workspaces.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-card p-6 border border-white/60">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Notes</h3>
              <p className="text-gray-600">Capture and organize lecture content with structured notes.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-card p-6 border border-white/60">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Flashcards</h3>
              <p className="text-gray-600">Automatically generate flashcards from your notes for efficient studying.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-card p-6 border border-white/60">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Log In & Sign Up</h3>
              <p className="text-gray-600">Secure authentication with OAuth support for easy access.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-card p-6 border border-white/60">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Study Groups</h3>
              <p className="text-gray-600">Find and connect with study partners in your classes.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-card p-6 border border-white/60">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Social Network</h3>
              <p className="text-gray-600">Upvote, comment, and reply to engage with the community.</p>
            </div>
          </div>
        </div>

        {/* Meet the Team section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-[#4b2f23] mb-8 text-center">Meet the Team</h2>
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {teamMembers.map((member) => (
                <div key={member.id} className="bg-white rounded-2xl shadow-card p-8 border border-white/60 text-center">
                  {member.headshot_url ? (
                    <img
                      src={member.headshot_url}
                      alt={member.name}
                      className="w-64 h-64 rounded-xl mx-auto mb-4 object-cover"
                    />
                  ) : (
                    <div className="w-64 h-64 rounded-xl mx-auto mb-4 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-6xl font-bold">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  {member.university && (
                    <p className="text-gray-600 mb-1">{member.university}</p>
                  )}
                  {member.major && (
                    <p className="text-gray-600 mb-1">{member.major}</p>
                  )}
                  {member.class && (
                    <p className="text-gray-600 mb-6">{member.class}</p>
                  )}
                  {(!member.university && !member.major && !member.class) && (
                    <p className="text-gray-600 mb-6">&nbsp;</p>
                  )}
                  <div className="flex gap-3 justify-center flex-wrap">
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                        aria-label="Email"
                      >
                        {/* @ts-ignore - react-icons has known TypeScript strict mode issue in Vercel builds */}
                        <FaEnvelope size={20} />
                      </a>
                    )}
                    {member.linkedin_url && (
                      <a
                        href={member.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-[#0077b5] text-white rounded-lg hover:bg-[#006399] transition-colors flex items-center justify-center"
                        aria-label="LinkedIn"
                      >
                        {/* @ts-ignore - react-icons has known TypeScript strict mode issue in Vercel builds */}
                        <FaLinkedin size={20} />
                      </a>
                    )}
                    {member.github_url && (
                      <a
                        href={member.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
                        aria-label="GitHub"
                      >
                        {/* @ts-ignore - react-icons has known TypeScript strict mode issue in Vercel builds */}
                        <FaGithub size={20} />
                      </a>
                    )}
                    {member.resume_url && (
                      <a
                        href={member.resume_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium"
                      >
                        CV
                      </a>
                    )}
                    {member.website_url && (
                      <a
                        href={member.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                      >
                        Website
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PageContainer>
    </div>
  );
};

export default AboutPage;

